import pMemoize from "p-memoize";
import fetch from "cross-fetch";
async function checkSource(src) {
  var _a;
  try {
    if ((_a = await fetch(src, { method: "HEAD" })) == null ? void 0 : _a.ok) {
      return src;
    }
  } catch {
    return void 0;
  }
  return void 0;
}
async function getSources({ sources }) {
  let src = null;
  const validSources = sources == null ? void 0 : sources.filter(Boolean);
  const blob = validSources == null ? void 0 : validSources.find((source) => source.startsWith("blob:"));
  const file = validSources == null ? void 0 : validSources.find((source) => !source.startsWith("http") && !source.startsWith("blob:"));
  if (blob || file) {
    return blob ?? file;
  }
  if (validSources.length > 0) {
    const sPromises = validSources.map((src2) => checkSource(src2));
    src = (await Promise.all(sPromises)).find(Boolean);
  }
  return src;
}
async function readContent(url) {
  try {
    const response = await fetch(url);
    return response.text();
  } catch {
    return null;
  }
}
async function getContent(content) {
  content = content.trim();
  const isUrl = content.startsWith("http") || content.startsWith("blob:");
  return isUrl ? readContent(content) : (
    // If it's not a url assume it's the string content already
    content
  );
}
const getCaptions = pMemoize(
  async (captionSource) => {
    if (!captionSource) {
      return null;
    }
    return getContent(captionSource);
  },
  {
    cacheKey: ([source]) => source
  }
);
const getVideos = pMemoize(
  async (data) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
    const [src, playlist, preview] = await Promise.all([
      getSources({ sources: [(_b = (_a = data == null ? void 0 : data.answer) == null ? void 0 : _a.media) == null ? void 0 : _b.video, (_d = (_c = data == null ? void 0 : data.answer) == null ? void 0 : _c.media) == null ? void 0 : _d.input] }),
      getSources({ sources: [(_f = (_e = data == null ? void 0 : data.answer) == null ? void 0 : _e.media) == null ? void 0 : _f.playlist] }),
      getSources({ sources: [((_h = (_g = data == null ? void 0 : data.answer) == null ? void 0 : _g.media) == null ? void 0 : _h.reducedQuality) ?? ((_k = (_j = (_i = data == null ? void 0 : data.answer) == null ? void 0 : _i.media) == null ? void 0 : _j.videos) == null ? void 0 : _k.xs)] })
    ]);
    return {
      src,
      playlist,
      preview
    };
  },
  {
    cacheKey: ([data]) => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
      return JSON.stringify({
        video: (_b = (_a = data == null ? void 0 : data.answer) == null ? void 0 : _a.media) == null ? void 0 : _b.video,
        input: (_d = (_c = data == null ? void 0 : data.answer) == null ? void 0 : _c.media) == null ? void 0 : _d.input,
        playlist: (_f = (_e = data == null ? void 0 : data.answer) == null ? void 0 : _e.media) == null ? void 0 : _f.playlist,
        preview: ((_h = (_g = data == null ? void 0 : data.answer) == null ? void 0 : _g.media) == null ? void 0 : _h.reducedQuality) ?? ((_k = (_j = (_i = data == null ? void 0 : data.answer) == null ? void 0 : _i.media) == null ? void 0 : _j.videos) == null ? void 0 : _k.xs)
      });
    }
  }
);
function getNames(data) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
  const name = ((_b = (_a = data == null ? void 0 : data.answer) == null ? void 0 : _a.contact) == null ? void 0 : _b.name) === "Anonymous" ? "" : ((_e = (_d = (_c = data == null ? void 0 : data.answer) == null ? void 0 : _c.contact) == null ? void 0 : _d.name) == null ? void 0 : _e.trim()) ?? "";
  const companyName = ((_h = (_g = (_f = data == null ? void 0 : data.answer) == null ? void 0 : _f.contact) == null ? void 0 : _g.client) == null ? void 0 : _h.name) === "Not Supplied" ? "" : ((_l = (_k = (_j = (_i = data == null ? void 0 : data.answer) == null ? void 0 : _i.contact) == null ? void 0 : _j.client) == null ? void 0 : _k.name) == null ? void 0 : _l.trim()) ?? "";
  const [firstName, lastName] = name.split(" ");
  return {
    name,
    firstName,
    lastName,
    companyName
  };
}
async function getKey(data, rootData, index) {
  var _a, _b, _c;
  const ownId = (_a = rootData == null ? void 0 : rootData.answer) == null ? void 0 : _a.id;
  const questionCounts = (_c = (_b = data == null ? void 0 : data.questions) == null ? void 0 : _b.items) == null ? void 0 : _c.reduce((acc, question) => {
    var _a2;
    if (((_a2 = question == null ? void 0 : question.answer) == null ? void 0 : _a2.id) === ownId) {
      return (acc ?? 0) + 1;
    }
    return acc;
  }, 0);
  if (questionCounts <= 1) {
    return ownId;
  }
  return `${ownId}-${index}`;
}
async function deriveData({ data, rootData, index }) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n;
  const [key, videos, captions] = await Promise.all([
    getKey(data, rootData, index),
    getVideos(rootData),
    getCaptions(((_b = (_a = rootData == null ? void 0 : rootData.answer) == null ? void 0 : _a.media) == null ? void 0 : _b.captions) ?? ((_d = (_c = rootData == null ? void 0 : rootData.answer) == null ? void 0 : _c.captions) == null ? void 0 : _d.current))
  ]);
  const names = getNames(rootData);
  return {
    ...videos,
    ...names,
    key,
    captions,
    answerLabel: ((_e = rootData == null ? void 0 : rootData.answer) == null ? void 0 : _e.label) ?? (rootData == null ? void 0 : rootData.title),
    prevAnswerLabel: index ? ((_i = (_h = (_g = (_f = data == null ? void 0 : data.questions) == null ? void 0 : _f.items) == null ? void 0 : _g[index - 1]) == null ? void 0 : _h.answer) == null ? void 0 : _i.label) ?? ((_l = (_k = (_j = data == null ? void 0 : data.questions) == null ? void 0 : _j.items) == null ? void 0 : _k[index - 1]) == null ? void 0 : _l.title) ?? null : null,
    isFirstInstanceOfName: index ? ((_n = (_m = data == null ? void 0 : data.questions) == null ? void 0 : _m.items) == null ? void 0 : _n.findIndex((otherQuestion) => getNames(otherQuestion).name === names.name)) === index : true
  };
}
export {
  deriveData
};
//# sourceMappingURL=video-utils-legacy.js.map
